/// <reference types="@types/google.maps" />
import { inject, Injectable } from '@angular/core';
import { catchAndLogError } from '@rma/generic/rxjs/operators/catch-and-log-error';
import { BaseAddress } from '@rma/public/generic/domain/types';
import { set } from 'lodash-es';
import { from, map, Observable, of } from 'rxjs';
import { GOOGLE_MAPS_REGION_CODE } from './google-maps.provider';

@Injectable({ providedIn: 'root' })
export class GoogleMapsAutoCompleteSuggestionService {
  private readonly regionCode = inject(GOOGLE_MAPS_REGION_CODE);

  readonly googleMappings: Record<string, string> = {
    subpremise: 'unitNumber',
    street_number: 'streetNumber',
    route: 'street',
    // locality is the city/town/suburb for AU/NZ/US/CA; UK/IE/SE return it under postal_town and would need a fallback
    locality: 'locality',
    administrative_area_level_1: 'state',
    country: 'country',
    postal_code: 'postcode',
  };

  private geocoder: google.maps.Geocoder | undefined;

  search(input: string): Observable<google.maps.places.PlacePrediction[]> {
    const sessionToken = new google.maps.places.AutocompleteSessionToken();

    const request: google.maps.places.AutocompleteRequest = {
      includedRegionCodes: [this.regionCode],
      includedPrimaryTypes: ['street_address'],
      input,
      sessionToken,
    };

    return from(google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)).pipe(
      catchAndLogError<{
        suggestions: google.maps.places.AutocompleteSuggestion[];
      }>(of({ suggestions: [] })),
      map(({ suggestions }) => suggestions.map(({ placePrediction }) => placePrediction).filter((x) => x !== null)),
    );
  }

  mapPredictionToType<T extends BaseAddress>(results: google.maps.GeocoderResult[]): T | undefined {
    const result = results[0];

    if (!result?.address_components) {
      return;
    }

    const address = {
      coords: result.geometry
        ? {
            latitude: result.geometry.location.lat(),
            longitude: result.geometry.location.lng(),
          }
        : undefined,
    };

    result.address_components.forEach((component) => {
      component.types.forEach((type) => {
        if (Object.prototype.hasOwnProperty.call(this.googleMappings, type)) {
          set(address, this.googleMappings[type], component.short_name);
        }
      });
    });

    // SAFETY(legacy): unreviewed, pre-dates this rule.
    return address as T;
  }

  addressToGeocoder(address: string): Observable<google.maps.GeocoderResult[]> {
    const request: google.maps.GeocoderRequest = {
      address,
    };

    return new Observable((obs) => {
      const callback = (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        switch (status) {
          case google.maps.GeocoderStatus.ZERO_RESULTS:
            obs.next([]);
            obs.complete();
            break;

          case google.maps.GeocoderStatus.OK:
            obs.next(results ?? []);
            obs.complete();
            break;

          default:
            obs.error(status);
            break;
        }
      };

      const service = (this.geocoder ||= new google.maps.Geocoder());
      service.geocode(request, callback);
    });
  }
}
